import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/cn';
import { Icons } from '@/shared/components/ui/icons';
import { Input, type InputProps } from '@/shared/components/ui/input';

export type PasswordInputProps = InputProps & {
  toggleClassName?: string;
};

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, id, toggleClassName, ...props }, ref) => {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);

    return (
      <div className="relative">
        <Input
          ref={ref}
          id={id}
          type={visible ? 'text' : 'password'}
          className={cn('pe-11', className)}
          {...props}
        />
        <button
          type="button"
          className={cn(
            'absolute inset-y-0 end-0 flex items-center justify-center px-3',
            'text-muted-foreground transition-colors hover:text-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-e-lg',
            toggleClassName,
          )}
          onClick={() => setVisible((v) => !v)}
          aria-label={
            visible ? t('auth.passwordVisibility.hide') : t('auth.passwordVisibility.show')
          }
          aria-pressed={visible}
          aria-controls={id}
        >
          {visible ? (
            <Icons.Eye className="h-4 w-4" aria-hidden />
          ) : (
            <Icons.EyeOff className="h-4 w-4" aria-hidden />
          )}
        </button>
      </div>
    );
  },
);
PasswordInput.displayName = 'PasswordInput';
